'use client';

import { useEffect, useState } from 'react';
import { ChevronRight, ScanSearch } from 'lucide-react';
import { PromoClickLink } from '@/components/marketing/promo-click-link';
import { DPI_CHECKER_PROMO_ID, dpiCheckerHref } from '@/lib/tools/dpi-checker-link';
import { parseDimensions } from '@/lib/utils/dimensions';

const COPY = {
  nl: { title: 'Twijfel je of je foto scherp genoeg is?', text: 'Check gratis welk formaat je foto aankan.', sized: 'Check gratis of je foto scherp wordt op {size}.' },
  en: { title: 'Not sure your photo is sharp enough?', text: 'Check for free which sizes your photo can handle.', sized: 'Check for free if your photo prints sharp at {size}.' },
  de: { title: 'Unsicher, ob Ihr Foto scharf genug ist?', text: 'Prüfen Sie kostenlos, welche Formate Ihr Foto schafft.', sized: 'Prüfen Sie kostenlos, ob Ihr Foto in {size} scharf wird.' },
  fr: { title: 'Votre photo est-elle assez nette ?', text: 'Vérifiez gratuitement les formats qu’elle permet.', sized: 'Vérifiez gratuitement si elle sera nette en {size}.' },
} as const;

type Size = { widthMm: number; heightMm: number };

function sizeFrom(value: unknown): Size | null {
  if (typeof value !== 'string' || !value) return null;
  const parsed = parseDimensions(value);
  return parsed && parsed.width > 0 && parsed.height > 0 ? { widthMm: parsed.width, heightMm: parsed.height } : null;
}

function formatCm(mm: number, locale: string) {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(mm / 10);
}

/**
 * Rustige verwijzing naar de DPI-checker onder de configurator. Het gekozen
 * formaat komt uit het bestaande `printen:pricing`-event; opent in een nieuw
 * tabblad zodat de samengestelde configuratie en upload blijven staan.
 */
export function DpiCheckCallout({ productId, productSlug, locale }: { productId: string; productSlug: string; locale: string }) {
  const t = COPY[locale as keyof typeof COPY] || COPY.en;
  const [size, setSize] = useState<Size | null>(null);

  useEffect(() => {
    const update = (event: Event) => {
      const detail = (event as CustomEvent).detail as { productId?: unknown; size?: unknown } | undefined;
      if (!detail || detail.productId !== productId || !('size' in detail)) return;
      setSize(sizeFrom(detail.size));
    };
    window.addEventListener('printen:pricing', update);
    return () => window.removeEventListener('printen:pricing', update);
  }, [productId]);

  const href = `/${locale}${dpiCheckerHref({ widthMm: size?.widthMm, heightMm: size?.heightMm, productSlug })}`;
  const sizeLabel = size ? `${formatCm(size.widthMm, locale)} x ${formatCm(size.heightMm, locale)} cm` : null;

  return (
    <PromoClickLink
      href={href}
      promoId={DPI_CHECKER_PROMO_ID}
      promoName="DPI checker"
      slot="product"
      target="_blank"
      rel="noopener"
      className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 text-left transition hover:border-blue-300 hover:bg-blue-50/40"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
        <ScanSearch className="h-5 w-5" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-bold text-slate-900">{t.title}</span>
        <span className="block min-h-[1.25rem] text-[13px] leading-5 text-slate-600">
          {sizeLabel ? t.sized.replace('{size}', sizeLabel) : t.text}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-blue-500" aria-hidden="true" />
    </PromoClickLink>
  );
}
